import React from 'react';
import { CircularProgress, Typography } from '@mui/material';
import { useStore } from '../../store/store';
import { LOGIN_NONCE_PREFLIGHT_ATTRIBUTE } from '../../helpers/login-nonce-preflight';

// Reuses sign-in-to-view's classes below for the spinner/failure content, per
// the visual language it already establishes — imported directly since this
// overlay renders on every route, including ones that never mount SignInToView.
import '../sign-in-to-view/sign-in-to-view.scss';
import './login-nonce-overlay.scss';

const block = 'login-nonce-overlay';

// Rendered from wrapRootElement so it covers every route, including ones with
// no Contentful page template. Also consumed by navigation-menu.tsx,
// sign-in-to-view.tsx, and {contentfulPageTemplate.slug}.tsx, all of which
// read loginNonceStatus to keep their own access gates up during a redeem.
const LoginNonceOverlay: React.FC = () => {
  const loginNonceStatus = useStore((state) => state.loginNonceStatus);

  // Hand the pre-hydration cover (see gatsby-ssr.tsx) over to this component.
  // While redeeming both are up and identical, so there is nothing to see in
  // the swap; the attribute has to be cleared afterwards or its
  // `visibility: hidden` would keep the page hidden for good.
  React.useEffect(() => {
    if (loginNonceStatus !== 'redeeming') {
      document.documentElement.removeAttribute(LOGIN_NONCE_PREFLIGHT_ATTRIBUTE);
    }
  }, [ loginNonceStatus ]);

  if (loginNonceStatus === 'idle') {
    return null;
  }

  const handleSignInClick = () => {
    // Dismiss the overlay in the same update that opens the popup, so the two
    // never render on top of each other.
    useStore.setState({ loginNonceStatus: 'idle', showLoginPopup: true });
  };

  return (
    <div
      className={block}
      role="alert"
      aria-live="assertive"
      aria-busy={loginNonceStatus === 'redeeming'}
    >
      {loginNonceStatus === 'redeeming' ? (
        <div className="sign-in-to-view__checking-access">
          <CircularProgress />
          <Typography variant="h4" gutterBottom>
            Signing you in...
          </Typography>
        </div>
      ) : (
        <div className="sign-in-to-view__not-logged-in-wrapper">
          <Typography variant="h3" gutterBottom>
            We couldn&apos;t sign you in
          </Typography>
          <div className="sign-in-to-view__buttons">
            <button className="primary h5" onClick={handleSignInClick}>
              Sign in
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginNonceOverlay;
